import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, Download, CalendarDays, LayoutGrid, LogIn, ListTree, CheckCircle2, AlertTriangle, AlertOctagon,
  MessagesSquare, ArrowRightLeft, Moon, MessageCircleWarning, Timer, Clock, Bot, Users, ChevronDown,
} from 'lucide-react';
import { jarvis, ESTADOS, formatarTelefone, duracao } from './api';
import './relatorios.css';

// Relatórios do Teste Jarvis com rastreabilidade completa: login/logout no módulo,
// por atendente e cada atendimento com a linha do tempo das etapas (fila → assumiu →
// 1ª resposta → transferência/encerramento).
const SAIDAS = { encerrada: 'Encerrou', transferida_fila: 'Transferiu p/ fila', transferida_atendente: 'Transferiu p/ atendente', puxada: 'Puxada por outro' };
const ORIGENS = { fila: 'Fila', transferida_fila: 'Transferência (fila)', transferida_atendente: 'Transferência (atendente)', puxada: 'Puxou de outro', iniciada: 'Iniciou a conversa' };
const ms = (a, b) => (a && b ? new Date(b) - new Date(a) : null);
const media = l => { const v = l.filter(x => x != null && x >= 0); return v.length ? v.reduce((s, x) => s + x, 0) / v.length : null; };
const soma = l => l.filter(x => x != null && x >= 0).reduce((s, x) => s + x, 0);
const dataHora = iso => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—');

function baixarCSV(nome, linhas) {
  const csv = '﻿' + linhas.map(l => l.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = nome;
  a.click();
}

function calcular(dados, nomeFila) {
  const agoraMs = new Date(dados.agora).getTime();
  const conv = dados.conversas;
  const etapasPor = {};
  dados.etapas.forEach(e => { (etapasPor[e.conversa_id] = etapasPor[e.conversa_id] || []).push(e); });

  // Sessões: fim = saída, ou último sinal se parou de sinalizar (inatividade)
  const sessoes = dados.sessoes.map(s => {
    const ativa = !s.fim && agoraMs - new Date(s.ultimo_sinal).getTime() < dados.inativoMs;
    const fimEf = s.fim || (ativa ? null : s.ultimo_sinal);
    return { ...s, ativa, fimEf, motivo: ativa ? 'conectado' : (s.fim_motivo === 'saiu' ? 'saiu' : 'inatividade'), dur: ms(s.inicio, fimEf || dados.agora) };
  });

  const atend = conv.map(c => {
    const etapas = etapasPor[c.id] || [];
    return {
      ...c, etapas,
      robo: ms(c.criado_em, c.entrou_fila_em),
      esperaPrimeira: ms(c.entrou_fila_em || c.criado_em, c.primeira_resposta_em),
      duracao: ms(c.criado_em, c.encerrado_em),
      atendentes: [...new Set(etapas.map(e => e.atendente_nome).filter(Boolean))],
    };
  });

  const porAt = {};
  const pega = (email, nome) => (porAt[email] = porAt[email] || { email, nome: nome || email, etapas: [], sessoes: [] });
  dados.etapas.filter(e => e.atendente_email).forEach(e => pega(e.atendente_email, e.atendente_nome).etapas.push(e));
  sessoes.forEach(s => pega(s.email, s.nome).sessoes.push(s));
  const atendentes = Object.values(porAt).map(a => {
    const assumidas = a.etapas.filter(e => e.assumido_em);
    const ultima = a.sessoes[a.sessoes.length - 1];
    return {
      ...a,
      logado: soma(a.sessoes.map(s => s.dur)),
      primeiraEntrada: a.sessoes[0]?.inicio,
      conectado: a.sessoes.some(s => s.ativa),
      ultimaSaida: ultima?.fimEf || null,
      atendimentos: assumidas.length,
      encerrou: a.etapas.filter(e => e.saida === 'encerrada').length,
      transferiu: a.etapas.filter(e => e.saida === 'transferida_fila' || e.saida === 'transferida_atendente').length,
      recebeuTransf: a.etapas.filter(e => e.origem === 'transferida_fila' || e.origem === 'transferida_atendente').length,
      emAberto: a.etapas.filter(e => !e.saiu_em).length,
      mAssumir: media(assumidas.map(e => ms(e.entrou_em, e.assumido_em))),
      mResposta: media(a.etapas.map(e => ms(e.entrou_em, e.primeira_resposta_em))),
      mAtendimento: media(a.etapas.filter(e => e.saiu_em).map(e => ms(e.assumido_em, e.saiu_em))),
      tAtendimento: soma(a.etapas.map(e => ms(e.assumido_em, e.saiu_em || dados.agora))),
    };
  }).sort((x, y) => y.atendimentos - x.atendimentos);

  const contar = (f, lista = conv) => { const m = {}; lista.forEach(x => { const k = f(x) || '—'; m[k] = (m[k] || 0) + 1; }); return Object.entries(m).sort((a, b) => b[1] - a[1]); };
  return {
    sessoes, atend, atendentes,
    total: conv.length,
    encerradas: conv.filter(c => c.estado === 'encerrada').length,
    foraHorario: conv.filter(c => c.fora_horario).length,
    transferidas: conv.filter(c => c.transferencias > 0).length,
    semResposta: conv.filter(c => !c.primeira_resposta_em && c.estado !== 'bot').length,
    mEspera: media(atend.map(a => a.esperaPrimeira)),
    mDuracao: media(atend.map(a => a.duracao)),
    mRobo: media(atend.map(a => a.robo)),
    porFila: contar(c => nomeFila(c.fila_id)),
    porCategoria: contar(c => c.categoria || 'Sem categoria', conv.filter(c => c.estado === 'encerrada')),
  };
}

export default function Relatorios({ setErro }) {
  const hoje = new Date(Date.now() - 3 * 3600000).toISOString().slice(0, 10);
  const [de, setDe] = useState(new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10));
  const [ate, setAte] = useState(hoje);
  const [dados, setDados] = useState(null);
  const [filas, setFilas] = useState([]);
  const [sub, setSub] = useState('resumo');
  const [aberto, setAberto] = useState(null);

  const buscar = useCallback(() => {
    setDados(null);
    jarvis('relatorio', { de, ate }).then(setDados).catch(e => setErro(e.message));
  }, [de, ate, setErro]);
  useEffect(() => { buscar(); jarvis('filasTodas').then(r => setFilas(r.filas)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const nomeFila = useCallback(id => filas.find(f => f.id === id)?.nome || 'Sem fila', [filas]);
  const r = useMemo(() => (dados ? calcular(dados, nomeFila) : null), [dados, nomeFila]);

  const exportar = () => {
    if (!r) return;
    const arq = s => `jarvis-${s}-${de}-a-${ate}.csv`;
    if (sub === 'resumo') {
      baixarCSV(arq('resumo'), [
        ['Período', `${de} a ${ate}`], ['Conversas', r.total], ['Encerradas', r.encerradas], ['Com transferência', r.transferidas],
        ['Fora do horário', r.foraHorario], ['Sem resposta', r.semResposta], ['Espera média até 1ª resposta', duracao(r.mEspera)],
        ['Duração média', duracao(r.mDuracao)], ['Tempo médio no robô', duracao(r.mRobo)],
        [], ['Fila', 'Conversas'], ...r.porFila, [], ['Categoria', 'Conversas'], ...r.porCategoria,
      ]);
    } else if (sub === 'atendentes') {
      baixarCSV(arq('atendentes'), [
        ['Atendente', 'E-mail', 'Tempo logado', 'Sessões', '1ª entrada', 'Última saída', 'Atendimentos', 'Encerrou', 'Transferiu', 'Recebeu transferidas', 'Em aberto', 'Média até assumir', 'Média até 1ª resposta', 'Média em atendimento', 'Total em atendimento'],
        ...r.atendentes.map(a => [a.nome, a.email, duracao(a.logado), a.sessoes.length, dataHora(a.primeiraEntrada), a.conectado ? 'conectado' : dataHora(a.ultimaSaida), a.atendimentos, a.encerrou, a.transferiu, a.recebeuTransf, a.emAberto, duracao(a.mAssumir), duracao(a.mResposta), duracao(a.mAtendimento), duracao(a.tAtendimento)]),
      ]);
    } else if (sub === 'sessoes') {
      baixarCSV(arq('login-logout'), [
        ['Colaborador', 'E-mail', 'Entrou', 'Saiu', 'Como saiu', 'Tempo logado'],
        ...r.sessoes.map(s => [s.nome, s.email, dataHora(s.inicio), s.fimEf ? dataHora(s.fimEf) : '—', s.motivo, duracao(s.dur)]),
      ]);
    } else {
      baixarCSV(arq('atendimentos'), [
        ['Protocolo', 'Contato', 'Telefone', 'Fila final', 'Situação', 'Categoria', 'Fora do horário', 'Chegou', 'Tempo no robô', 'Entrou na fila', '1ª resposta', 'Espera até 1ª resposta', 'Encerrou', 'Duração total', 'Transferências',
          'Etapa', 'Origem', 'Fila da etapa', 'Atendente', 'Entrou na espera', 'Assumiu/abriu', 'Até assumir', '1ª resposta na etapa', 'Até responder', 'Saiu', 'Como saiu', 'Por', 'Para', 'Tempo em atendimento'],
        ...r.atend.flatMap(c => {
          const base = [c.protocolo, c.cooperado_nome || c.nome, c.telefone, nomeFila(c.fila_id), ESTADOS[c.estado]?.rotulo, c.categoria, c.fora_horario ? 'sim' : 'não', dataHora(c.criado_em), duracao(c.robo), dataHora(c.entrou_fila_em), dataHora(c.primeira_resposta_em), duracao(c.esperaPrimeira), dataHora(c.encerrado_em), duracao(c.duracao), c.transferencias];
          if (!c.etapas.length) return [base];
          return c.etapas.map((e, i) => [...base, i + 1, ORIGENS[e.origem] || e.origem, nomeFila(e.fila_id), e.atendente_nome, dataHora(e.entrou_em), dataHora(e.assumido_em), duracao(ms(e.entrou_em, e.assumido_em)), dataHora(e.primeira_resposta_em), duracao(ms(e.entrou_em, e.primeira_resposta_em)), dataHora(e.saiu_em), SAIDAS[e.saida] || (e.saiu_em ? e.saida : 'em andamento'), e.saida_por, e.saida_para, duracao(ms(e.assumido_em, e.saiu_em))]);
        }),
      ]);
    }
  };

  return (
    <div className="jv-rel">
      <div className="jv-rel-topo">
        <div>
          <h3>Relatórios</h3>
          <span className="jv-sub">Rastreabilidade de cada atendimento, atendente e acesso ao módulo</span>
        </div>
        <div className="jv-rel-filtros">
          <div className="jv-periodo">
            <CalendarDays size={16} />
            <input type="date" value={de} onChange={e => setDe(e.target.value)} />
            <span>até</span>
            <input type="date" value={ate} max={hoje} onChange={e => setAte(e.target.value)} />
          </div>
          <button className="jv-btn" onClick={buscar}><RefreshCw size={16} /> Buscar</button>
          <button className="jv-btn" disabled={!r} onClick={exportar}><Download size={16} /> Excel</button>
        </div>
      </div>

      <div className="jv-segmentos">
        {[['resumo', 'Resumo', LayoutGrid], ['atendentes', 'Por atendente', Users], ['sessoes', 'Login e logout', LogIn], ['atendimentos', 'Atendimentos', ListTree]].map(([k, t, Icone]) => (
          <button key={k} className={sub === k ? 'ativa' : ''} onClick={() => setSub(k)}><Icone size={15} /> {t}</button>
        ))}
      </div>

      {!r ? <div className="jv-carregando">Carregando…</div> : (
        <>
          {sub === 'resumo' && <Resumo r={r} />}
          {sub === 'atendentes' && <PorAtendente r={r} />}
          {sub === 'sessoes' && <Sessoes r={r} />}
          {sub === 'atendimentos' && <Atendimentos r={r} nomeFila={nomeFila} aberto={aberto} setAberto={setAberto} />}
        </>
      )}
    </div>
  );
}

// Peças exportadas também para prévia/teste fora da tela
export { calcular, Resumo, PorAtendente, Sessoes, Atendimentos };

// ── Peças visuais ─────────────────────────────────────────────────────────────

// Selo de rapidez para tempos de espera (com ícone e texto, nunca só cor)
function selo(msVal) {
  if (msVal == null) return null;
  if (msVal <= 2 * 60000) return { cls: 'bom', txt: 'rápido', Icone: CheckCircle2 };
  if (msVal <= 5 * 60000) return { cls: 'atencao', txt: 'atenção', Icone: AlertTriangle };
  return { cls: 'lento', txt: 'lento', Icone: AlertOctagon };
}
function Selo({ ms: v }) {
  const s = selo(v);
  if (!s) return null;
  return <span className={`jv-selo ${s.cls}`}><s.Icone size={12} /> {s.txt}</span>;
}

function Kpi({ Icone, valor, rotulo, extra }) {
  return (
    <div className="jv-kpi">
      <div className="jv-kpi-icone"><Icone size={18} /></div>
      <div className="jv-kpi-valor">{valor}</div>
      <div className="jv-kpi-rotulo">{rotulo}</div>
      {extra && <div className="jv-kpi-extra">{extra}</div>}
    </div>
  );
}

function Barras({ titulo, itens }) {
  const max = Math.max(1, ...itens.map(([, v]) => v));
  return (
    <div className="jv-painel">
      <h4>{titulo}</h4>
      {itens.length === 0 && <div className="jv-vazio">Sem dados no período.</div>}
      {itens.map(([k, v]) => (
        <div key={k} className="jv-barra-linha" title={`${k}: ${v}`}>
          <span className="jv-barra-rotulo">{k}</span>
          <div className="jv-barra-trilho"><div className="jv-barra-valor" style={{ width: `${(v / max) * 100}%` }} /></div>
          <b>{v}</b>
        </div>
      ))}
    </div>
  );
}

function Resumo({ r }) {
  return (
    <>
      <div className="jv-grupo-titulo">Volume</div>
      <div className="jv-kpis">
        <Kpi Icone={MessagesSquare} valor={r.total} rotulo="Conversas" />
        <Kpi Icone={CheckCircle2} valor={r.encerradas} rotulo="Encerradas" />
        <Kpi Icone={ArrowRightLeft} valor={r.transferidas} rotulo="Com transferência" />
        <Kpi Icone={Moon} valor={r.foraHorario} rotulo="Fora do horário" />
        <Kpi Icone={MessageCircleWarning} valor={r.semResposta} rotulo="Sem resposta ainda" />
      </div>
      <div className="jv-grupo-titulo">Tempos médios</div>
      <div className="jv-kpis jv-kpis-3">
        <Kpi Icone={Timer} valor={duracao(r.mEspera)} rotulo="Espera até a 1ª resposta" extra={<Selo ms={r.mEspera} />} />
        <Kpi Icone={Clock} valor={duracao(r.mDuracao)} rotulo="Duração (chegou → encerrou)" />
        <Kpi Icone={Bot} valor={duracao(r.mRobo)} rotulo="No robô (até escolher a fila)" />
      </div>
      <div className="jv-grade-2">
        <Barras titulo="Conversas por fila" itens={r.porFila} />
        <Barras titulo="Encerramentos por categoria" itens={r.porCategoria} />
      </div>
    </>
  );
}

function Medidor({ rotulo, valor, max, comSelo }) {
  const pct = valor == null ? 0 : Math.max(4, Math.min(100, (valor / max) * 100));
  return (
    <div className="jv-medidor" title={`${rotulo}: ${duracao(valor)}`}>
      <div className="jv-medidor-cab"><span>{rotulo}</span><b>{duracao(valor)}</b>{comSelo && <Selo ms={valor} />}</div>
      <div className="jv-barra-trilho"><div className="jv-barra-valor" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function PorAtendente({ r }) {
  if (!r.atendentes.length) return <div className="jv-vazio-grande"><Users size={40} /><p>Ninguém atendeu nem entrou no módulo no período.</p></div>;
  const maxT = Math.max(60000, ...r.atendentes.flatMap(a => [a.mAssumir, a.mResposta, a.mAtendimento].filter(v => v != null)));
  return (
    <div className="jv-cartoes-at">
      {r.atendentes.map(a => (
        <div key={a.email} className="jv-cartao-at">
          <div className="jv-at-cab">
            <div className="jv-avatar jv-avatar-m">{(a.nome || '?').slice(0, 1).toUpperCase()}</div>
            <div className="jv-at-nome">
              <strong>{a.nome}</strong>
              {a.conectado
                ? <span className="jv-pilula on">● conectado agora</span>
                : <span className="jv-pilula">{a.ultimaSaida ? `saiu ${dataHora(a.ultimaSaida)}` : 'sem login registrado'}</span>}
            </div>
            <div className="jv-at-logado">
              <b>{duracao(a.logado)}</b>
              <span>logado · {a.sessoes.length} {a.sessoes.length === 1 ? 'sessão' : 'sessões'}</span>
            </div>
          </div>

          <div className="jv-at-numeros">
            <div><b>{a.atendimentos}</b><span>atendimentos</span></div>
            <div><b>{a.encerrou}</b><span>encerrou</span></div>
            <div><b>{a.transferiu}</b><span>transferiu</span></div>
            <div><b>{a.recebeuTransf}</b><span>recebeu transf.</span></div>
            <div className={a.emAberto ? 'destaque' : ''}><b>{a.emAberto}</b><span>em aberto</span></div>
          </div>

          <div className="jv-at-tempos">
            <Medidor rotulo="Até assumir" valor={a.mAssumir} max={maxT} comSelo />
            <Medidor rotulo="Até a 1ª resposta" valor={a.mResposta} max={maxT} comSelo />
            <Medidor rotulo="Em atendimento (média)" valor={a.mAtendimento} max={maxT} />
          </div>

          <div className="jv-at-rodape">
            <span><LogIn size={13} /> 1ª entrada {dataHora(a.primeiraEntrada)}</span>
            <span><Clock size={13} /> {duracao(a.tAtendimento)} em atendimento no total</span>
          </div>
        </div>
      ))}
      <p className="jv-sub jv-nota">Atendimentos = etapas que a pessoa assumiu (uma conversa transferida conta para cada um que atendeu). Os tempos contam desde que a conversa entrou na espera dela. Selo: até 2 min rápido · até 5 min atenção · acima disso lento.</p>
    </div>
  );
}

// Régua do dia (0h–24h, horário de Brasília) com os períodos conectados
function Sessoes({ r }) {
  if (!r.sessoes.length) return <div className="jv-vazio-grande"><LogIn size={40} /><p>Ninguém abriu a tela de Operador no período.</p></div>;
  const dia = iso => new Date(new Date(iso).getTime() - 3 * 3600000).toISOString().slice(0, 10);
  const minutoDoDia = iso => { const d = new Date(new Date(iso).getTime() - 3 * 3600000); return d.getUTCHours() * 60 + d.getUTCMinutes(); };
  const grupos = {};
  r.sessoes.forEach(s => { const k = `${dia(s.inicio)}|${s.email}`; (grupos[k] = grupos[k] || { dia: dia(s.inicio), nome: s.nome || s.email, lista: [] }).lista.push(s); });
  const linhas = Object.values(grupos).sort((x, y) => y.dia.localeCompare(x.dia) || x.nome.localeCompare(y.nome));
  return (
    <div className="jv-sessoes">
      <div className="jv-regua-horas"><span />{[0, 6, 12, 18, 24].map(h => <i key={h} style={{ left: `${(h / 24) * 100}%` }}>{h}h</i>)}</div>
      {linhas.map(g => (
        <div key={g.dia + g.nome} className="jv-sessao-linha">
          <div className="jv-sessao-quem">
            <div className="jv-avatar jv-avatar-p">{g.nome.slice(0, 1).toUpperCase()}</div>
            <div><strong>{g.nome}</strong><span className="jv-sub">{g.dia.split('-').reverse().join('/')} · {duracao(soma(g.lista.map(s => s.dur)))}</span></div>
          </div>
          <div className="jv-regua">
            {[6, 12, 18].map(h => <i key={h} style={{ left: `${(h / 24) * 100}%` }} />)}
            {g.lista.map(s => {
              const ini = minutoDoDia(s.inicio);
              const fim = s.fimEf && dia(s.fimEf) === g.dia ? minutoDoDia(s.fimEf) : (s.ativa ? minutoDoDia(new Date().toISOString()) : 1440);
              return (
                <div key={s.id} className={`jv-regua-bloco ${s.ativa ? 'ativa' : ''}`}
                  style={{ left: `${(ini / 1440) * 100}%`, width: `${Math.max(0.6, ((fim - ini) / 1440) * 100)}%` }}
                  title={`Entrou ${dataHora(s.inicio)} · ${s.ativa ? 'conectado agora' : `saiu ${dataHora(s.fimEf)} (${s.motivo === 'saiu' ? 'saiu do módulo' : 'inatividade'})`} · ${duracao(s.dur)}`} />
              );
            })}
          </div>
          <div className="jv-sessao-lista">
            {g.lista.map(s => (
              <span key={s.id} className="jv-sessao-item">
                {dataHora(s.inicio).slice(-8, -3)} → {s.ativa ? <b className="jv-verde">agora</b> : dataHora(s.fimEf).slice(-8, -3)}
                <em className={s.ativa ? 'on' : s.motivo === 'saiu' ? '' : 'inativo'}>{s.ativa ? 'conectado' : s.motivo === 'saiu' ? 'saiu' : 'inatividade'}</em>
              </span>
            ))}
          </div>
        </div>
      ))}
      <p className="jv-sub jv-nota">Passe o mouse num bloco para ver os horários. "Inatividade" = fechou o navegador ou perdeu a conexão (a saída conta no último sinal da tela, até 3 min depois).</p>
    </div>
  );
}

function Atendimentos({ r, nomeFila, aberto, setAberto }) {
  if (!r.atend.length) return <div className="jv-vazio-grande"><ListTree size={40} /><p>Nenhuma conversa no período.</p></div>;
  return (
    <div className="jv-atend-lista">
      {r.atend.slice().reverse().map(c => {
        const abrir = aberto === c.id;
        return (
          <div key={c.id} className={`jv-atend ${abrir ? 'aberto' : ''}`}>
            <button className="jv-atend-linha" onClick={() => setAberto(abrir ? null : c.id)}>
              <span className="jv-protocolo">#{c.protocolo}</span>
              <div className="jv-atend-quem">
                <strong>{c.cooperado_nome || c.nome || formatarTelefone(c.telefone)} {c.fora_horario && <Moon size={13} />}</strong>
                <span className="jv-sub">{nomeFila(c.fila_id)} · {dataHora(c.criado_em)}</span>
              </div>
              <div className="jv-atend-col"><span className="jv-sub">1ª resposta</span><b>{c.primeira_resposta_em ? duracao(c.esperaPrimeira) : '—'}</b>{c.primeira_resposta_em && <Selo ms={c.esperaPrimeira} />}</div>
              <div className="jv-atend-col"><span className="jv-sub">Duração</span><b>{duracao(c.duracao)}</b></div>
              <div className="jv-atend-col jv-atend-pessoas"><span className="jv-sub">Atendentes</span><b>{c.atendentes.join(' → ') || '—'}</b></div>
              <span className="jv-pilula" style={{ color: ESTADOS[c.estado]?.cor }}>{c.categoria || ESTADOS[c.estado]?.rotulo}</span>
              <ChevronDown size={18} className="jv-seta" />
            </button>
            {abrir && (
              <div className="jv-linha-tempo">
                <Ponto cor="cinza" titulo="Chegou" quando={c.criado_em} />
                {c.entrou_fila_em && <Ponto cor="cinza" titulo={`Escolheu a fila no robô (${duracao(c.robo)})`} quando={c.entrou_fila_em} />}
                {c.etapas.length === 0 && <div className="jv-sub jv-lt-aviso">Sem etapas registradas.</div>}
                {c.etapas.map((e, i) => (
                  <div key={e.id} className="jv-lt-etapa">
                    <div className="jv-lt-etapa-cab">
                      <span className="jv-lt-num">{i + 1}</span>
                      <strong>{e.atendente_nome || 'Sem atendente'}</strong>
                      <span className="jv-sub">{ORIGENS[e.origem] || e.origem}{e.origem_por ? ` · por ${e.origem_por}` : ''} · {nomeFila(e.fila_id)}</span>
                    </div>
                    <Ponto titulo="Entrou na espera" quando={e.entrou_em} />
                    <Ponto titulo="Assumiu / abriu" quando={e.assumido_em} tempo={ms(e.entrou_em, e.assumido_em)} />
                    <Ponto titulo="1ª resposta" quando={e.primeira_resposta_em} tempo={ms(e.entrou_em, e.primeira_resposta_em)} />
                    <Ponto cor={e.saida === 'encerrada' ? 'fim' : e.saiu_em ? 'transf' : 'aberto'}
                      titulo={e.saiu_em ? `${SAIDAS[e.saida] || e.saida}${e.saida_para ? ` → ${e.saida_para}` : ''}` : 'Em andamento'}
                      quando={e.saiu_em} tempo={ms(e.assumido_em, e.saiu_em)} tempoRotulo="em atendimento" />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Ponto({ titulo, quando, tempo, tempoRotulo = 'depois', cor = '' }) {
  return (
    <div className={`jv-lt-ponto ${cor} ${quando ? '' : 'vazio'}`}>
      <i />
      <span>{titulo}</span>
      <b>{quando ? dataHora(quando) : '—'}</b>
      {tempo != null && <em>{duracao(tempo)} {tempoRotulo}</em>}
    </div>
  );
}
